import React, { useState } from 'react'

import { Box, Button, Card, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  AtsJobPostingSettingsSmartActionsItemFragment,
  AtsToggleSmartActionCandidateEmailDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteSmartAction } from './DeleteSmartAction'
import { EditSmartAction } from './EditSmartAction'
import { ItemDetails } from './ItemDetails'

interface Props {
  title: string
  selectedSmartAction: AtsJobPostingSettingsSmartActionsItemFragment
  smartActions: AtsJobPostingSettingsSmartActionsItemFragment[]
}

export const Item = ({ title, selectedSmartAction, smartActions }: Props) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditSidePanel, setShowEditSidePanel] = useState(false)
  const { company } = useCurrentSession()

  const [toggleSmartAction] = useMutation(AtsToggleSmartActionCandidateEmailDocument, {
    extractorFn: (data) => data.atsMutations.toggleEnabledAtsCandidateEmailAction,
  })

  const handleOnChange = async (value: boolean) => {
    const result = await toggleSmartAction({
      variables: {
        id: selectedSmartAction.id,
        enabled: value,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      if (!selectedSmartAction.enabled) {
        track(trackingEvents.ats.smartActionActivate, {
          action_type: selectedSmartAction.actionType,
          company_id: company.id,
          job_position: selectedSmartAction.atsJobPosting.id,
        })
      }
    }
  }

  return (
    <>
      <Card>
        <Card.Content>
          <Box
            width="full"
            flexDirection="row"
            justifyContent="spaceBetween"
            gap="s24"
            paddingBottom="s12"
            alignItems="center"
          >
            <Text.Normal color="grey1000" weight="semibold">
              {title}
            </Text.Normal>

            <Box flexDirection="row" gap="s12">
              <TogglerCheckbox
                value={selectedSmartAction.enabled}
                onChange={(value) => handleOnChange(value)}
                aria-label={i18n.t(
                  'ats.job_posting_settings.smart_actions.actions.toggle_checkbox_label'
                )}
              />
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative
                    type="secondary"
                    iconName="ellipsis"
                    aria-label={i18n.t(
                      'ats.job_posting_settings.smart_actions.actions.more_actions'
                    )}
                  />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center">
                  <PopoverMenu.TouchableOption
                    onClick={() => setShowEditSidePanel(true)}
                    title={i18n.t('common.edit')}
                  />
                  <PopoverMenu.TouchableOption
                    onClick={() => setShowDeleteModal(true)}
                    title={i18n.t('common.destroy')}
                    danger
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            </Box>
          </Box>
          <ItemDetails item={selectedSmartAction} />
        </Card.Content>
      </Card>

      {showDeleteModal && (
        <DeleteSmartAction id={selectedSmartAction.id} onClose={() => setShowDeleteModal(false)} />
      )}

      {showEditSidePanel && (
        <EditSmartAction
          selectedSmartAction={selectedSmartAction}
          smartActions={smartActions}
          open={showEditSidePanel}
          onClose={() => setShowEditSidePanel(false)}
        />
      )}
    </>
  )
}
